import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import api from "@/lib/api";
import { Users, Briefcase, Building2, Megaphone } from "lucide-react";

const ICONS = {
  candidate: Users,
  job: Briefcase,
  client: Building2,
  announcement: Megaphone,
};
const ROUTES = {
  candidate: "/candidates/",
  job: "/jobs",
  client: "/clients",
  announcement: "/communications?announcement=",
};

export default function CommandPalette({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  const search = useCallback(async (value) => {
    if (!value || value.length < 2) {
      setResults([]);
      return;
    }
    try {
      const { data } = await api.get("/meta/search", { params: { q: value } });
      setResults(data.results || []);
    } catch {
      setResults([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q, search]);

  const go = (r) => {
    setOpen(false);
    setQ("");
    if (r.type === "candidate") navigate(`/candidates/${r.id}`);
    else if (r.type === "announcement")
      navigate(`/communications?announcement=${r.id}`);
    else navigate(ROUTES[r.type] || "/");
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        data-testid="command-palette-input"
        placeholder="Search records and announcements…"
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        <CommandEmpty>
          {q.length < 2 ? "Type to search across Nexreq…" : "No results found."}
        </CommandEmpty>
        {results.length > 0 && (
          <CommandGroup heading="Results">
            {results.map((r) => {
              const Icon = ICONS[r.type] || Users;
              return (
                <CommandItem
                  key={`${r.type}-${r.id}`}
                  onSelect={() => go(r)}
                  value={`${r.title}-${r.id}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-[#255058]" />
                  <span className="font-medium">{r.title}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {r.subtitle}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
